import React from "react";
import graphics from "../helpers/graphics";
import ControllableObject from "./controllableObject";
import SimManager from "../managers/simManager";
import "./styles.scss";
import { Vector3, Condition } from "../types";
import { calculateXOffset, calculateYOffset } from "../helpers/responsiveness";
import CustomObject from "./customObject";
import InteractableObject, { IOProps } from "./interactableObject";
import { triggerButtonAction } from "./interactableButtons";

export interface SubObject {
  id: string;
  name: string;
  onclick?: any;
  onDropItems?: any;
  position?: Vector3 | { x: string; y: string; z: string };
  buttons?: string[];
}

export interface CompositeIOProps extends IOProps {
  objects?: SubObject[];
}

interface SubObjectVisual {
  id: string;
  element: React.JSX.Element;
}

class CompositeInteractableObject extends InteractableObject {
  private subObjects: Map<string, SubObject> = new Map();
  private subObjectVisuals: Map<string, SubObjectVisual> = new Map();

  constructor(props: CompositeIOProps) {
    super(props);

    // Initialize sub-objects
    if (props.objects) {
      props.objects.forEach((subObj) => {
        this.subObjects.set(subObj.id, subObj);

        // Create visual representation if position is provided
        if (subObj.position) {
          this.createSubObjectVisual(subObj);
        }
      });
    }
  }

  componentDidMount(): void {
    super.componentDidMount();
    // Register this composite object with SimManager
    SimManager.getInstance().registerCompositeInteractableObject(this);
  }

  // Create visual representation for a sub-object
  private createSubObjectVisual = (subObj: SubObject) => {
    if (!subObj.position) {
      return;
    }

    // Get the image for this sub-object based on current parent state
    const imageName = this.getSubObjectImageName(subObj.id);
    if (!imageName) {
      return;
    }

    // Load the image for the sub-object
    const image = graphics
      .getInstance()
      .loadImage(
        imageName,
        `${subObj.name} in ${this.getCurrentState()} state`,
        [],
        this.state.userScale,
        0.766,
        this.state.objectScale
      );

    if (!image) {
      return;
    }

    const subObjectElement = this.createSubObjectElement(subObj, image);

    if (!subObjectElement) {
      return;
    }

    const visual: SubObjectVisual = {
      id: subObj.id,
      element: subObjectElement,
    };

    this.subObjectVisuals.set(subObj.id, visual);
  };

  // Get the image name for a sub-object based on current parent state
  private getSubObjectImageName = (subObjectId: string): string | undefined => {
    const currentState = this.getCurrentState();
    const currentStateDefinition = (this as any).states.find(
      (state: any) => state.name === currentState
    );

    if (!currentStateDefinition || !currentStateDefinition.objects) {
      return undefined;
    }

    // Look for the sub-object in the current state's objects array
    const objectEntry = currentStateDefinition.objects.find((obj: any) =>
      Object.prototype.hasOwnProperty.call(obj, subObjectId)
    );

    return objectEntry ? (objectEntry as any)[subObjectId] : undefined;
  };

  // Create the JSX element for a sub-object
  private createSubObjectElement = (
    subObj: SubObject,
    image: React.JSX.Element | undefined
  ) => {
    if (!image) {
      return null;
    }
    const position = subObj.position!;

    const CSSStyle: React.CSSProperties = {
      position: "absolute",
      cursor: "pointer",
      userSelect: "none",
      left: calculateXOffset(position.x as number),
      top: calculateYOffset(position.y as number),
      zIndex: position.z || 0,
      scale: `calc(${this.state.objectScale || 1})`,
      transition: "opacity 0.2s ease-in-out",
    };

    const handleClick = () => {
      this.handleSubObjectClick(subObj.id);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    };

    const handleMouseEnter = () => {
      // Trigger parent's hover state to show control tooltip
      this.triggerParentHover(subObj.id);
    };

    const handleMouseLeave = () => {
      // Trigger parent's hover leave state to hide control tooltip
      this.triggerParentHoverLeave();
    };

    const handleFocus = () => {
      // Trigger parent's hover state to show control tooltip on focus
      this.triggerParentHover(subObj.id);
    };

    const handleBlur = () => {
      // Trigger parent's hover leave state to hide control tooltip on blur
      this.triggerParentHoverLeave();
    };

    return (
      <div
        key={subObj.id}
        id={subObj.id}
        style={CSSStyle}
        className="graphics sub-object"
        role="button"
        tabIndex={0}
        aria-label={subObj.name || subObj.id}
        data-object-scale={this.state.objectScale}
        data-parent-state={this.getCurrentState()}
        data-object-id={this.getId()}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onMouseDown={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {image}
      </div>
    );
  };

  // Update all sub-object visuals when parent state changes
  private updateAllSubObjectVisuals = () => {
    this.subObjects.forEach((subObj) => {
      if (subObj.position) {
        this.createSubObjectVisual(subObj);
      }
    });
    // Force re-render
    this.forceUpdate();
  };

  // Override parent's setItemState to update sub-object visuals when parent state changes
  setItemState = (postState: string) => {
    // Call the parent method directly instead of using super
    const changeState = (this as any).states.filter(
      (x: { name: string }) => x.name === postState
    );
    if (changeState.length > 0) {
      SimManager.getInstance().updateAltTextFunction(this.props.id);
      const image =
        graphics
          .getInstance()
          .loadImage(
            changeState[0].image,
            changeState[0].altText || "",
            [],
            this.state.userScale,
            0.766,
            this.state.objectScale
          ) || null;
      this.setState(
        {
          image: image,
          currentState: postState,
          ejectAltText: changeState[0].ejectAltText
            ? changeState[0].ejectAltText
            : "",
        },
        () => {
          // Update all sub-object visuals to match new parent state
          this.updateAllSubObjectVisuals();
          SimManager.getInstance().handleUpdateItems();
        }
      );
      return true;
    }
    return false;
  };

  // Handle clicks on sub-objects
  handleSubObjectClick = (subObjectId: string) => {
    const subObject = this.subObjects.get(subObjectId);
    if (!subObject) {
      return;
    }

    // If sub-object has buttons array, trigger the matched button's action
    if (subObject.buttons && subObject.buttons.length > 0) {
      const currentButtons = this.getButtons();
      if (currentButtons) {
        for (const buttonTitle of subObject.buttons) {
          const matchedButton = currentButtons.find(
            (btn: any) =>
              (typeof btn === "string" ? btn : btn.title) === buttonTitle
          );
          if (matchedButton) {
            triggerButtonAction(
              matchedButton,
              this.getId(),
              this.changeText.bind(this)
            );
            break;
          }
        }
      }
    }

    // Process onclick actions independently
    if (subObject.onclick) {
      const currentParentState = this.getCurrentState();

      // Process onclick actions for the sub-object
      subObject.onclick.forEach((element: any) => {
        let shouldProcess = false;

        // Determine if this action should be processed based on the target item and its current state
        if (element.item === this.getId()) {
          // For parent updates, check against parent's current state
          shouldProcess = element.initialState === currentParentState;
        } else {
          // For other object updates, check against that object's current state
          const otherObjectState = SimManager.getInstance().getState(
            element.item
          );
          shouldProcess = element.initialState === otherObjectState;
        }

        if (shouldProcess) {
          let conditionsChecked = false;
          if (element.conditions) {
            element.conditions.forEach((condition: Condition) => {
              // Check conditions for state changes
              let itemToCheck = condition.conditionItem;
              const stateToCheck = condition.conditionState;

              if (itemToCheck === "Self") {
                itemToCheck = this.getId();
              }

              const currentState =
                itemToCheck === this.getId()
                  ? this.getCurrentState()
                  : SimManager.getInstance().getState(itemToCheck);

              if (currentState === stateToCheck) {
                conditionsChecked = true;
              }
            });
          } else {
            conditionsChecked = true;
          }

          if (conditionsChecked) {
            // Handle different item types
            if (element.item === this.getId()) {
              // Update the parent object
              this.setItemState(element.postState);
            } else {
              // Update another object via SimManager
              SimManager.getInstance().setState(
                element.item,
                element.postState
              );
            }
          }
        }
      });
    }
  };

  // Handle drops on sub-objects
  handleSubObjectDrop = (
    subObjectId: string,
    item: ControllableObject | CustomObject
  ) => {
    const subObject = this.subObjects.get(subObjectId);
    if (!subObject || !subObject.onDropItems) {
      return;
    }

    const currentParentState = this.getCurrentState();

    subObject.onDropItems.forEach((element: any) => {
      if (element.item === this.getId()) {
        // Handle drops that affect the parent object
        if (
          element.droppedItemName.includes(item.getId()) &&
          (currentParentState === element.initialState ||
            (!element.initialState &&
              (!element.initialStates ||
                element.initialStates?.includes(currentParentState))))
        ) {
          let conditionsChecked = false;
          if (!element.conditions) {
            this.setItemState(element.postState);
            SimManager.dispatchAction(element.actionName, {
              id: this.getId(),
              droppedId: item.getId(),
            });
            element.message &&
              SimManager.getInstance().addToast(
                element.message.title,
                element.message.message,
                element.message.type
              );
          } else {
            element.conditions.forEach((condition: Condition) => {
              const itemId =
                condition.conditionItem === "Dropped"
                  ? item.getId()
                  : this.getId();

              if (
                SimManager.getInstance().checkCondition({
                  ...condition,
                  conditionItem: itemId,
                })
              ) {
                conditionsChecked = true;
              }
            });
          }

          if (conditionsChecked) {
            this.setItemState(element.postState);
            this.setState({
              hover: "buttons",
              isHovered: true,
            });
            SimManager.dispatchAction(element.actionName, {
              id: this.getId(),
              droppedId: item.getId(),
            });
            element.message &&
              SimManager.getInstance().addToast(
                element.message.title,
                element.message.message,
                element.message.type
              );
          }
        }
      } else if (element.item === "Dropped") {
        // Handle drops that affect the dropped item
        if (element.droppedItemName.includes(item.getId())) {
          if (
            item.getCurrentState() === element.initialState ||
            (!element.initialState &&
              (!element.initialStates ||
                element.initialStates?.includes(item.getCurrentState())))
          ) {
            let conditionsChecked = false;
            if (!element.conditions) {
              SimManager.updateObjectState(
                item.getId(),
                (
                  element.transitions?.find(
                    (x: any) => x.initialState === item.getCurrentState()
                  ) || element
                ).postState,
                undefined,
                element.resetPosition
              );
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            } else {
              element.conditions.forEach((condition: Condition) => {
                const itemId =
                  condition.conditionItem === "Dropped"
                    ? item.getId()
                    : this.getId();

                if (
                  SimManager.getInstance().checkCondition({
                    ...condition,
                    conditionItem: itemId,
                  })
                )
                  conditionsChecked = true;
              });
            }

            if (conditionsChecked) {
              SimManager.updateObjectState(
                item.getId(),
                (
                  element.transitions?.find(
                    (x: any) => x.initialState === item.getCurrentState()
                  ) || element
                ).postState,
                undefined,
                element.resetPosition
              );
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            }
          }
        }
      }
    });
  };

  // Override the parent's onItemDropped to handle composite logic
  onItemDropped = (item: ControllableObject | CustomObject) => {
    // Call parent logic using the InteractableObject's onItemDropped method
    // This processes the parent object's own onDropItems configuration
    if ((this as any).onDropItems) {
      (this as any).onDropItems.forEach((element: any) => {
        if (element.item == "Self") {
          if (
            element.droppedItemName.includes(item.getId()) &&
            (this.state.currentState == element.initialState ||
              (!element.initialState &&
                (!element.initialStates ||
                  element.initialStates?.includes(this.state.currentState))))
          ) {
            let conditionsChecked = false;
            if (!element.conditions) {
              this.setItemState(
                (
                  element.transitions?.find(
                    (x: any) => x.initialState === this.state.currentState
                  ) || element
                ).postState
              );
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            } else {
              element.conditions.forEach((condition: Condition) => {
                const itemId =
                  condition.conditionItem === "Dropped"
                    ? item.getId()
                    : this.getId();

                if (
                  SimManager.getInstance().checkCondition({
                    ...condition,
                    conditionItem: itemId,
                  })
                ) {
                  conditionsChecked = true;
                }
              });
            }

            if (conditionsChecked) {
              this.setItemState(
                (
                  element.transitions?.find(
                    (x: any) => x.initialState === this.state.currentState
                  ) || element
                ).postState
              );
              this.setState({
                hover: "buttons",
                isHovered: true,
              });
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            }
          }
        } else if (element.item == "Dropped") {
          if (element.droppedItemName.includes(item.getId())) {
            if (
              item.getCurrentState() === element.initialState ||
              (!element.initialState &&
                (!element.initialStates ||
                  element.initialStates?.includes(item.getCurrentState())))
            ) {
              let conditionsChecked = false;
              if (!element.conditions) {
                SimManager.updateObjectState(
                  item.getId(),
                  (
                    element.transitions?.find(
                      (x: any) => x.initialState === item.getCurrentState()
                    ) || element
                  ).postState,
                  undefined,
                  element.resetPosition
                );
                SimManager.dispatchAction(element.actionName, {
                  id: this.getId(),
                  droppedId: item.getId(),
                });
                element.message &&
                  SimManager.getInstance().addToast(
                    element.message.title,
                    element.message.message,
                    element.message.type
                  );
              } else {
                element.conditions.forEach((condition: Condition) => {
                  const itemId =
                    condition.conditionItem === "Dropped"
                      ? item.getId()
                      : this.getId();

                  if (
                    SimManager.getInstance().checkCondition({
                      ...condition,
                      conditionItem: itemId,
                    })
                  )
                    conditionsChecked = true;
                });
              }

              if (conditionsChecked) {
                SimManager.updateObjectState(
                  item.getId(),
                  (
                    element.transitions?.find(
                      (x: any) => x.initialState === item.getCurrentState()
                    ) || element
                  ).postState,
                  undefined,
                  element.resetPosition
                );
                SimManager.dispatchAction(element.actionName, {
                  id: this.getId(),
                  droppedId: item.getId(),
                });
                element.message &&
                  SimManager.getInstance().addToast(
                    element.message.title,
                    element.message.message,
                    element.message.type
                  );
              }
            }
          }
        }
      });
    }

    // Then handle sub-object drop logic
    this.subObjects.forEach((_subObject, subObjectId) => {
      this.handleSubObjectDrop(subObjectId, item);
    });
  };

  // Get all sub-object IDs
  getSubObjectIds = (): string[] => {
    return Array.from(this.subObjects.keys());
  };

  // Get sub-object by ID
  getSubObject = (id: string): SubObject | undefined => {
    return this.subObjects.get(id);
  };

  // Trigger parent hover state when sub-object is hovered
  private triggerParentHover = (subObjectId?: string) => {
    // Check if parent has buttons available to show tooltip
    const buttons = this.getButtons();

    if (buttons && buttons.length > 0) {
      // Update state to show hover/buttons
      this.setState({
        hover: "buttons",
        isHovered: true,
      });

      // Apply shadow effect to matching buttons if sub-object has buttons list
      if (subObjectId) {
        // Add a small delay to ensure tooltip is rendered before applying shadows
        setTimeout(() => {
          this.applyShadowToMatchingButtons(subObjectId);
        }, 50);
      }
    }
  };

  // Apply shadow effect to buttons that match the sub-object's buttons list
  private applyShadowToMatchingButtons = (subObjectId: string) => {
    const subObject = this.subObjects.get(subObjectId);
    if (!subObject || !subObject.buttons) {
      return;
    }

    const subObjectButtons = subObject.buttons;
    const currentStateButtons = this.getButtons();

    if (currentStateButtons && subObjectButtons) {
      // Find matching buttons and apply shadow

      subObjectButtons.forEach((buttonTitle) => {
        const matchingButton = currentStateButtons.find(
          (btn: any) => btn.title === buttonTitle || btn === buttonTitle
        );

        if (matchingButton) {
          const buttonTitle =
            typeof matchingButton === "string"
              ? matchingButton
              : matchingButton.title;

          // Apply shadow CSS class to matching button using data-title attribute
          const selectors = [
            `[data-title="${buttonTitle}"][class*="controlsTooltip_item"]`,
            `.interactable-tooltip [data-title="${buttonTitle}"][class*="controlsTooltip_item"]`,
            `.tooltip [data-title="${buttonTitle}"][class*="controlsTooltip_item"]`,
            `[id="${this.getId()}"] ~ .interactable-tooltip [data-title="${buttonTitle}"][class*="controlsTooltip_item"]`,
            `[data-title="${buttonTitle}"][role="button"][tabindex="0"]`,
            `[data-title="${buttonTitle}"].controlsTooltip_item`,
          ];

          let targetElement: Element | null = null;

          for (const selector of selectors) {
            targetElement = document.querySelector(selector);
            if (targetElement) {
              break;
            }
          }

          if (targetElement) {
            targetElement.classList.add("button-highlight-object");
          }
        }
      });
    }
  };

  // Remove shadow effects from buttons
  private removeShadowFromButtons = () => {
    // Remove highlight from all button elements that might have it
    const buttonElements = document.querySelectorAll(
      ".button-highlight-object"
    );

    buttonElements.forEach((element) => {
      element.classList.remove("button-highlight-object");
    });
  };

  // Trigger parent hover leave state when sub-object hover ends
  private triggerParentHoverLeave = () => {
    // Remove shadow effects
    this.removeShadowFromButtons();

    // Reset to label state
    this.setState({
      hover: "label",
      isHovered: false,
    });
  };

  render() {
    const parentRender = super.render();

    // Get all sub-object visual elements
    const subObjectElements = Array.from(this.subObjectVisuals.values()).map(
      (visual) => visual.element
    );

    // If no sub-objects, just return parent render
    if (subObjectElements.length === 0) {
      return parentRender;
    }

    // Return parent render with sub-objects
    return (
      <>
        {parentRender}
        {subObjectElements}
      </>
    );
  }
}

export default CompositeInteractableObject;
